import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { AYUDA } from '../config/ayuda';
import { useAuth } from '../context/AuthContext';
import { usePerfil } from '../context/PerfilContext';
import { Logo } from '../components/Logo';
import { ArrowLeft, ArrowRight, ChevronDown, Search } from 'lucide-react';
import { agrupar } from '../utils/texto';

/**
 * Guía de uso. Pública (sin iniciar sesión), para poder mandarla a quien
 * todavía no tiene cuenta. El contenido vive en src/config/ayuda.ts.
 */
export const Ayuda: React.FC = () => {
  const { user } = useAuth();
  const { tieneModulo } = usePerfil();
  const { hash } = useLocation();
  // Las secciones de módulos más particulares solo para quien los tiene
  const disponibles = useMemo(
    () => AYUDA.filter(s => !s.soloConModulo || tieneModulo(s.soloConModulo)), [tieneModulo]);
  const [busqueda, setBusqueda] = useState('');
  const [abiertas, setAbiertas] = useState<Set<string>>(new Set());

  // /ayuda#amedias-invitar abre esa pregunta y baja hasta ella
  useEffect(() => {
    const id = decodeURIComponent(hash.replace('#', ''));
    if (!id) return;
    setAbiertas(prev => new Set(prev).add(id));
    setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
  }, [hash]);

  const normalizar = (t: string) => t.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

  const secciones = useMemo(() => {
    const q = normalizar(busqueda.trim());
    if (!q) return disponibles;
    return disponibles
      .map(s => ({ ...s, preguntas: s.preguntas.filter(p => normalizar(`${p.pregunta} ${p.respuesta.join(' ')}`).includes(q)) }))
      .filter(s => s.preguntas.length > 0);
  }, [busqueda, disponibles]);

  const alternar = (id: string) => setAbiertas(prev => {
    const n = new Set(prev);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* seguro-arriba: sin él, en el iPhone en vertical la cabecera (y el botón
          de volver) queda debajo de la barra de estado y la isla */}
      <header className="sticky top-0 z-10 bg-white/90 dark:bg-gray-800/90 backdrop-blur border-b border-gray-200
                         dark:border-gray-700 seguro-arriba seguro-lados">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center gap-3">
          <Logo size={28} />
          <span className="font-bold text-gray-900 dark:text-white flex-1">Ayuda</span>
          <Link to={user ? '/' : '/login'}
            className="inline-flex items-center gap-1 text-sm font-semibold text-emerald-700 dark:text-emerald-400 hover:underline">
            <ArrowLeft className="w-4 h-4" /> {user ? 'Volver a la app' : 'Entrar'}
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-6 seguro-abajo seguro-lados">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">¿En qué te ayudamos?</h1>
        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
          Respuestas cortas a lo más habitual. Pulsa una pregunta para verla.
        </p>

        <div className="relative mt-4">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input type="search" value={busqueda} onChange={e => setBusqueda(e.target.value)}
            placeholder="Buscar: invitar, ticket, recordatorio…"
            className="w-full pl-9 pr-3 py-2.5 rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800
                       text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500" />
        </div>

        {!busqueda && (
          <nav className="flex flex-wrap gap-2 mt-4" aria-label="Secciones">
            {disponibles.map(s => (
              <a key={s.id} href={`#${s.id}`}
                className="px-3 py-1.5 rounded-full text-xs font-semibold bg-white dark:bg-gray-800 border border-gray-200
                           dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:border-emerald-400">
                {s.emoji} {s.titulo}
              </a>
            ))}
          </nav>
        )}

        {secciones.length === 0 && (
          <p className="mt-8 text-center text-gray-500 dark:text-gray-400">No hay nada sobre «{busqueda}». Prueba con otra palabra.</p>
        )}

        {secciones.map(s => (
          <section key={s.id} id={s.id} className="mt-8 scroll-mt-20">
            <h2 className="text-lg font-bold text-gray-900 dark:text-white">{s.emoji} {s.titulo}</h2>
            {s.intro && <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{s.intro}</p>}
            <ul className="mt-3 space-y-2">
              {s.preguntas.map(p => {
                const abierta = abiertas.has(p.id) || !!busqueda;
                return (
                  <li key={p.id} id={p.id} className="scroll-mt-20 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700">
                    <button onClick={() => alternar(p.id)} aria-expanded={abierta}
                      className="w-full flex items-center gap-3 px-4 py-3 text-left">
                      <span className="flex-1 font-semibold text-gray-900 dark:text-white text-sm">{p.pregunta}</span>
                      <ChevronDown className={`w-4 h-4 text-gray-400 shrink-0 transition ${abierta ? 'rotate-180' : ''}`} />
                    </button>
                    {abierta && (
                      <div className="px-4 pb-4 space-y-2 text-sm text-gray-700 dark:text-gray-300">
                        {agrupar(p.respuesta).map((b, i) => Array.isArray(b) ? (
                          <ul key={i} className="list-disc pl-5 space-y-1">
                            {b.map((li, j) => <li key={j}>{li}</li>)}
                          </ul>
                        ) : <p key={i}>{b}</p>)}
                        {p.capturas && (
                          // Fila que se desliza de lado en el móvil, como una galería
                          <div className="flex gap-3 overflow-x-auto pt-2 pb-1 -mx-4 px-4 snap-x">
                            {p.capturas.map(c => (
                              <figure key={c.archivo} className="shrink-0 w-44 snap-start">
                                <img src={`/ayuda/${c.archivo}.webp`} alt={c.texto} loading="lazy"
                                  className="dark:hidden w-full rounded-xl border border-gray-200 dark:border-gray-700" />
                                <img src={`/ayuda/${c.archivo}-oscuro.webp`} alt={c.texto} loading="lazy"
                                  className="hidden dark:block w-full rounded-xl border border-gray-200 dark:border-gray-700" />
                                <figcaption className="mt-1.5 text-xs text-gray-500 dark:text-gray-400">{c.texto}</figcaption>
                              </figure>
                            ))}
                          </div>
                        )}
                        {p.accion && user && (!p.accion.modulo || tieneModulo(p.accion.modulo)) && (
                          <Link to={p.accion.ruta}
                            className="inline-flex items-center gap-1.5 mt-2 min-h-[44px] px-4 rounded-xl bg-emerald-600
                                       hover:bg-emerald-700 text-white font-semibold text-sm transition">
                            {p.accion.texto} <ArrowRight className="w-4 h-4" />
                          </Link>
                        )}
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          </section>
        ))}

        <p className="mt-10 text-center text-xs text-gray-500 dark:text-gray-400">
          ¿No encuentras lo que buscas? Pregúntale a quien te invitó o al administrador de la app.
        </p>
        <p className="mt-2 text-center text-xs text-gray-500 dark:text-gray-400">
          <Link to="/privacidad" className="hover:underline">Política de privacidad</Link>
        </p>
      </main>
    </div>
  );
};
